// FP3-061 — Provider Workboard page. Stand-alone scratch surface.
//
// Provider-scoped: every provider (school-team / solo / assistant)
// gets their OWN board. Assistants do NOT auto-inherit the
// supervising primary's board.
//
// Strictly decoupled from Caseload / Wizard / Health / Schedule /
// Goals / Reports / Fidelity. Soft-delete behind the scenes; the UI
// shows DELETE as final.

import { useState, useMemo, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  DotsThree,
  PencilSimple,
  Trash,
  ClipboardText,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import {
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  SOURCE_LABEL,
  STATUS_LABEL,
  STATUS_TONE_CLASS,
  PRIORITY_TONE_CLASS,
  toneClass,
  isWaitingStatus,
  isActiveItem,
  isDueSoon,
} from "@/lib/workboardConstants";
import WorkboardItemModal from "@/components/workboard/WorkboardItemModal";
// FP3-072 — Contextual Help Center deep-link.
import HelpLink from "@/components/help/HelpLink";
import PageShell from "@/components/layout/PageShell";

const ANY = "__any__";

export default function ProviderWorkboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(ANY);
  const [filterSource, setFilterSource] = useState(ANY);
  const [filterPriority, setFilterPriority] = useState(ANY);
  const [filterGrade, setFilterGrade] = useState("");
  const [sort, setSort] = useState("updated_at");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (filterStatus !== ANY) params.status = filterStatus;
      if (filterSource !== ANY) params.source_type = filterSource;
      if (filterPriority !== ANY) params.priority = filterPriority;
      if (filterGrade.trim()) params.grade = filterGrade.trim();
      if (search.trim()) params.search = search.trim();
      const r = await api.listWorkboardItems(params);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(`Failed to load workboard: ${e.response?.data?.detail || e.message}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSource, filterPriority, filterGrade, search, sort]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Summary card counts (derived from the currently loaded items, NOT
  // filtered, so the cards represent the caller's board state at a
  // glance even when filters narrow the table).
  // Implementation simplification: cards reflect FILTERED items so
  // the user can see "how many high-priority within my current
  // filter slice" — matches the user's "spreadsheet" mental model.
  const summary = useMemo(() => {
    const active = items.filter(isActiveItem);
    return {
      total_open: active.length,
      due_soon: items.filter(isDueSoon).length,
      high_priority: items.filter((i) => i.priority === "high" && isActiveItem(i)).length,
      waiting: items.filter((i) => isWaitingStatus(i.status)).length,
    };
  }, [items]);

  const openAdd = () => {
    setEditingItem(null);
    setModalOpen(true);
  };
  const openEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSave = async (draft) => {
    try {
      if (editingItem?.id) {
        await api.updateWorkboardItem(editingItem.id, draft);
        toast.success("Workboard item updated");
      } else {
        await api.createWorkboardItem(draft);
        toast.success("Workboard item added");
      }
      setModalOpen(false);
      setEditingItem(null);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const handleInlineUpdate = async (item, field, value) => {
    try {
      await api.updateWorkboardItem(item.id, { [field]: value });
      await load();
    } catch (e) {
      toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteWorkboardItem(deleteTarget.id);
      toast.success("Workboard item deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <PageShell testId="workboard-page" variant="table" className="animate-none">
      {/* Header — FP3-112F: title block above, Add-item action
          BELOW it (natural left-aligned). Dropped `justify-between`
          so Add-item never floats at the far-right edge of the
          bounded 1400px workboard shell — visually detached from
          the title it opens against. */}
      <div className="mb-6" data-testid="workboard-header">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardText size={24} weight="duotone" className="text-[#1E3F33]" />
            <h1 className="text-[26px] font-semibold text-[#1E3F33] heading-font">
              Workboard
            </h1>
          </div>
          <p className="text-[13px] text-[#6E6A60] mt-1 max-w-xl">
            Track students or tasks not yet on your active caseload — referrals, screenings,
            watchlist, evaluations in process. Notes here don&apos;t affect Caseload, Schedule,
            Health, Goals, or Reports.
          </p>
          {/* FP3-072 — Contextual help link to Workboard articles. */}
          <div className="mt-1.5">
            <HelpLink
              articleId="workboard-for"
              label="Learn what the Workboard is for"
              testId="workboard-help"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="workboard-header-actions">
          <Button
            data-testid="workboard-add-row-btn"
            onClick={openAdd}
            className="bg-[#1E3F33] hover:bg-[#2A4F42]"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Add item
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          testid="workboard-summary-total-open"
          label="Open items"
          value={summary.total_open}
        />
        <SummaryCard
          testid="workboard-summary-due-soon"
          label="Due within 7 days"
          value={summary.due_soon}
        />
        <SummaryCard
          testid="workboard-summary-high-priority"
          label="High priority"
          value={summary.high_priority}
          tone="high"
        />
        <SummaryCard
          testid="workboard-summary-waiting"
          label="Waiting on input"
          value={summary.waiting}
          tone="waiting"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8678] pointer-events-none"
          />
          <Input
            data-testid="workboard-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, teacher, notes…"
            className="pl-8 h-9 text-[13px]"
          />
        </div>

        <FilterSelect
          testid="workboard-filter-status"
          value={filterStatus}
          onChange={setFilterStatus}
          options={STATUS_OPTIONS}
          placeholder="Status"
        />
        <FilterSelect
          testid="workboard-filter-source"
          value={filterSource}
          onChange={setFilterSource}
          options={SOURCE_OPTIONS}
          placeholder="Source"
        />
        <FilterSelect
          testid="workboard-filter-priority"
          value={filterPriority}
          onChange={setFilterPriority}
          options={PRIORITY_OPTIONS}
          placeholder="Priority"
        />
        <Input
          data-testid="workboard-filter-grade"
          value={filterGrade}
          onChange={(e) => setFilterGrade(e.target.value)}
          placeholder="Grade"
          className="w-20 h-9 text-[13px]"
        />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="workboard-sort-select" className="w-[170px] h-9 text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at">Sort: Recently updated</SelectItem>
            <SelectItem value="due_date">Sort: Due date</SelectItem>
            <SelectItem value="follow_up_date">Sort: Follow-up date</SelectItem>
            <SelectItem value="referral_date">Sort: Referral date</SelectItem>
            <SelectItem value="created_at">Sort: Recently created</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-[#E5E3DB]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F4F2EC] text-[11px] uppercase tracking-[0.06em] text-[#6E6A60]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Student</th>
                <th className="text-left px-3 py-2 font-medium">Grade</th>
                <th className="text-left px-3 py-2 font-medium">Teacher</th>
                <th className="text-left px-3 py-2 font-medium">School</th>
                <th className="text-left px-3 py-2 font-medium">Source</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Priority</th>
                <th className="text-left px-3 py-2 font-medium">Follow-up</th>
                <th className="text-left px-3 py-2 font-medium">Due</th>
                <th className="text-left px-3 py-2 font-medium">Next step</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="workboard-row-list">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-[#8A8678] italic">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr data-testid="workboard-empty-state">
                  <td colSpan={11} className="px-3 py-10 text-center text-[#8A8678]">
                    <ClipboardText size={28} weight="duotone" className="inline-block text-[#C9C6BC] mb-2" />
                    <p className="text-[13px]">No workboard items yet.</p>
                    <p className="text-[11.5px] mt-1">
                      Click <span className="font-semibold">Add item</span> to track a referral, screening, or note.
                    </p>
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    onEdit={() => openEdit(item)}
                    onDelete={() => setDeleteTarget(item)}
                    onInlineUpdate={(field, value) => handleInlineUpdate(item, field, value)}
                  />
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit / create modal */}
      <WorkboardItemModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        onSave={handleSave}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="workboard-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workboard item?</AlertDialogTitle>
            <AlertDialogDescription>
              This only removes the item from your Provider Workboard. It does not delete
              any student record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="workboard-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="workboard-delete-confirm-btn"
              onClick={confirmDelete}
              className="bg-[#8E2415] hover:bg-[#751A0E]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function SummaryCard({ testid, label, value, tone }) {
  const toneClasses =
    tone === "high"
      ? "border-[#E1A290] bg-[#FCE8E2]"
      : tone === "waiting"
      ? "border-[#E5C779] bg-[#FDF3D8]"
      : "border-[#E5E3DB] bg-white";
  return (
    <Card data-testid={testid} className={`${toneClasses} border`}>
      <CardContent className="px-4 py-3">
        <p className="text-[10.5px] uppercase tracking-[0.08em] text-[#6E6A60] font-semibold">
          {label}
        </p>
        <p className="text-[26px] leading-tight font-semibold text-[#1E3F33] mt-0.5 heading-font">
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function FilterSelect({ testid, value, onChange, options, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-[160px] h-9 text-[13px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{placeholder}: Any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Row({ item, onEdit, onDelete, onInlineUpdate }) {
  const statusTone = toneClass(item.status, STATUS_OPTIONS, STATUS_TONE_CLASS);
  const priorityTone = toneClass(item.priority, PRIORITY_OPTIONS, PRIORITY_TONE_CLASS);
  return (
    <tr
      data-testid={`workboard-row-${item.id}`}
      className="border-t border-[#EEEBE3] hover:bg-[#FAF9F6] transition-colors"
    >
      <td className="px-3 py-2.5 font-medium text-[#1E3F33]">
        <button
          type="button"
          onClick={onEdit}
          className="hover:underline text-left"
          data-testid={`workboard-row-${item.id}-name-btn`}
        >
          {item.student_name_text}
        </button>
      </td>
      <td className="px-3 py-2.5 text-[#3F4A4A]">{item.grade || "—"}</td>
      <td className="px-3 py-2.5 text-[#3F4A4A]">{item.teacher_name || "—"}</td>
      <td className="px-3 py-2.5 text-[#3F4A4A]">{item.campus_text || "—"}</td>
      <td className="px-3 py-2.5 text-[#3F4A4A]">
        {SOURCE_LABEL[item.source_type] || item.source_type || "—"}
      </td>
      <td className="px-3 py-2.5">
        <InlineSelect
          testid={`workboard-row-${item.id}-status-select`}
          value={item.status || "new"}
          onChange={(v) => onInlineUpdate("status", v)}
          options={STATUS_OPTIONS}
          toneClass={statusTone}
          displayText={STATUS_LABEL[item.status] || item.status || "—"}
        />
      </td>
      <td className="px-3 py-2.5">
        <InlineSelect
          testid={`workboard-row-${item.id}-priority-select`}
          value={item.priority || "medium"}
          onChange={(v) => onInlineUpdate("priority", v)}
          options={PRIORITY_OPTIONS}
          toneClass={priorityTone}
          displayText={(item.priority || "medium").replace(/^./, (c) => c.toUpperCase())}
        />
      </td>
      <td className="px-3 py-2.5 text-[#3F4A4A] tabular-nums">{item.follow_up_date || "—"}</td>
      <td className="px-3 py-2.5 text-[#3F4A4A] tabular-nums">{item.due_date || "—"}</td>
      <td className="px-3 py-2.5 text-[#3F4A4A] max-w-[200px] truncate" title={item.next_step}>
        {item.next_step || "—"}
      </td>
      <td className="px-2 py-2.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid={`workboard-row-${item.id}-actions-btn`}
              className="p-1 rounded hover:bg-[#EEEBE3] text-[#6E6A60]"
              aria-label="Row actions"
            >
              <DotsThree size={18} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              data-testid={`workboard-row-${item.id}-edit-btn`}
              onClick={onEdit}
            >
              <PencilSimple size={14} className="mr-2" /> Edit details
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={`workboard-row-${item.id}-delete-btn`}
              onClick={onDelete}
              className="text-[#8E2415] focus:text-[#8E2415]"
            >
              <Trash size={14} className="mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

function InlineSelect({ testid, value, onChange, options, toneClass: tone, displayText }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className={`h-7 text-[11.5px] px-2 py-0.5 border ${tone} rounded-full w-fit min-w-[90px] gap-1`}
      >
        <SelectValue>{displayText}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-[12px]">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
